// Centralised SSR cache seeding.
//
// Called once from `pages/+Page.tsx` before the React tree mounts.
// Pre-warms react-query's cache with server-fetched data so hooks like
// `usePropertyQuery`, `useBootstrap`, `useFiltersQuery` resolve
// synchronously from cache without issuing duplicate network requests.
//
// KEY DESIGN:
// We use `setQueryDefaults({ staleTime: Infinity })` so the custom
// `useQuery` wrapper in `src/utils/query.ts` picks up the infinite
// staleTime via `queryClient.getQueryDefaults(key)`. This prevents
// background refetches for seeded queries.

import {
  transformFiltersToPreferences,
  transformFiltersToUnitsSearchParams,
  useInResiStore,
} from "@superbright/indexeddb-orm";

import { queryKeys } from "api/config";
import type { IQueryParams } from "types";
import { queryClient } from "utils";

import { type Data, hasListings } from "../../pages/_types";

/**
 * Seed a single query key so RQ v4 treats it as already fetched.
 *
 * `setQueryData` alone leaves the query's `state.dataUpdateCount` at 0.
 * When the first `useQuery` observer mounts, RQ sees no prior fetch and
 * calls `queryFn` regardless of `staleTime`.
 *
 * Fix: after setting data, bump `dataUpdateCount` on the cache entry so
 * RQ believes a real fetch completed. Combined with `staleTime: Infinity`
 * in query defaults, the observer will serve from cache without refetching.
 */
function seed(key: readonly unknown[], value: unknown): void {
  const k = key as unknown[];
  queryClient.setQueryDefaults(k, { staleTime: Infinity });
  queryClient.setQueryData(k, value);

  // Mark the query as "has been fetched" by bumping dataUpdateCount.
  const entry = queryClient.getQueryCache().find(k);
  if (entry) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = entry.state as any;
    if (state.dataUpdateCount === 0) {
      state.dataUpdateCount = 1;
    }
  }
}

/**
 * Seeds the react-query cache with data fetched server-side by `+data.ts`.
 *
 * After this runs, hooks that use the same query keys will find data
 * already in cache and won't fire their `queryFn`.
 *
 * `setQueryDefaults` is used to set `staleTime: Infinity` which the custom
 * `useQuery` wrapper merges with higher priority than its default 2000ms.
 * For hooks that use `useQuery` from `@tanstack/react-query` directly
 * (e.g. `useVisitorSession`, `useBootstrap`), they already set their own
 * `staleTime: Infinity`.
 */
export function seedCacheFromSSR(data: Data | undefined): void {
  if (!data) return;

  // ORM store mutations (setCurrentPropertyId / filterActions.set) touch the
  // client-only Zustand+IndexedDB store. Under SSR (ssr: true) this runs on the
  // server too, so guard store writes to the browser. The react-query seeding
  // below is DOM-free and runs on both server and client to produce SSR HTML.
  const isClient = typeof window !== "undefined";

  // ─── Property ────────────────────────────────────────────────────────
  if (data.slug && data.property) {
    seed(["property", data.slug], data.property);

    const propertyId = (data.property as { data?: { id?: unknown } } | null)
      ?.data?.id;
    if (isClient && propertyId != null) {
      void useInResiStore.getState().setCurrentPropertyId(String(propertyId));
    }
  }

  // ─── Visitor UUID ────────────────────────────────────────────────────
  if (data.visitor_uuid) {
    seed(queryKeys.visitorUUID(), data.visitor_uuid);
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────
  // safeFetch returns the full API envelope { data: { current_filter, ... } }
  // but fetchBootstrap (client queryFn) unwraps it to just the inner object.
  // Seed the unwrapped shape so consumers see the correct structure.
  if (data.slug && data.visitor_uuid && data.bootstrap) {
    const bootstrapInner =
      (data.bootstrap as { data?: unknown })?.data ?? data.bootstrap;
    seed(queryKeys.bootstrap(data.slug, data.visitor_uuid), bootstrapInner);
  }

  // ─── Filters ─────────────────────────────────────────────────────────
  // Filters are now URL-driven (see useUrlFilters), so the committed filters
  // are the same on server and client. Seed under the default-filters key
  // (empty URL → store defaults) and the server-computed key so the filters
  // query resolves from cache without a refetch.
  if (data.slug && data.filters) {
    const baseFilters = useInResiStore.getState().filters as Record<
      string,
      unknown
    >;

    const keysToSeed = new Set<string>();
    const seedFilterKey = (params: unknown) => {
      const key = ["filters", params, data.slug] as const;
      const id = JSON.stringify(key);
      if (keysToSeed.has(id)) return;
      keysToSeed.add(id);
      seed(key, data.filters);
    };

    seedFilterKey(transformFiltersToPreferences(baseFilters));
    if (data.filters_api_params) {
      seedFilterKey(data.filters_api_params);
    }
  }

  // ─── Listings (Units) ──────────────────────────────────────────────
  // Only seeded for the results page when the server was able to fetch.
  if (
    data.slug &&
    hasListings(data) &&
    data.listings &&
    data.listings_api_params
  ) {
    // Seed under the server-computed key (bootstrap filters or URL filters).
    const listingsKey = [
      ...queryKeys.listings(data.listings_api_params as IQueryParams),
      data.slug,
    ] as const;
    seed(listingsKey, data.listings);

    // Also seed under the empty-filters key so the client's first render
    // (before useUrlFiltersSync writes bootstrap filters to the URL) gets
    // a cache hit and avoids a redundant refetch.
    const emptyParams = transformFiltersToUnitsSearchParams({
      date_availability: undefined,
      qty_bedrooms: undefined,
      base_price: undefined,
      highlights: undefined,
    }) as IQueryParams;
    const emptyListingsKey = [
      ...queryKeys.listings(emptyParams),
      data.slug,
    ] as const;
    if (JSON.stringify(emptyListingsKey) !== JSON.stringify(listingsKey)) {
      seed(emptyListingsKey, data.listings);
    }
  }
}
